'use client';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { classNames } from '../../helpers/classNames';
import Button from '../Button';
import ButtonVariant, { BtnVariant } from '../ButtonVariant';
import styles from './styles.module.scss';

export interface DropdownButtonInterface {
    label: string;
    onClick?: () => void;
    href?: string;
    icon?: IconProp;
    variant?: BtnVariant;
}
interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    items: DropdownButtonInterface[];
    align?: 'left' | 'right';
    variant?: BtnVariant;
    className?: string;
    buttonClassName?: string;
    dropdownClassName?: string;
}

export function DropdownButton(props: Props) {
    const {
        children,
        items,
        align = 'left',
        className = '',
        buttonClassName = '',
        variant = 'primary',
        dropdownClassName = '',
    } = props;
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    return (
        <div
            className={classNames([className, styles.dropdownContainer])}
            ref={ref}
        >
            <Button
                variant={variant}
                className={buttonClassName}
                onClick={() => setOpen(!open)}
            >
                {children}
            </Button>
            {open && (
                <div
                    className={classNames([
                        styles.dropdownMenu,
                        align === 'right' ? styles.dropdownMenuRight : '',
                        dropdownClassName,
                    ])}
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="dropdownButton"
                    tabIndex={-1}
                >
                    <div className={styles.dropdownMenuItems} role="none">
                        {items.map((item, index) => (
                            <ButtonVariant
                                key={index}
                                variant={item.variant}
                                link={item.href}
                                className={styles.dropdownItem}
                                role="menuitem"
                                icon={item.icon}
                                tabIndex={-1}
                                onClick={() => {
                                    setOpen(false);
                                    item.onClick?.();
                                }}
                            >
                                {item.label}
                            </ButtonVariant>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
